// Per-case-study extras: mega-menu tagline, live product viz, FAQs, SEO meta.
// Keyed by slug. Use together with /data/caseStudies.js.

export const CASE_EXTRAS = {
  edtech: {
    tagline: "AI counsellor for admissions, status & follow-ups",
    productViz: {
      title: "Aria · Admission Counsellor",
      meta: "Hindi-EN · Vidyasetu University",
      transcript: [
        { who: "agent", text: "Namaste, this is Aria from Vidyasetu University." },
        { who: "caller", text: "Hi, I want to know about the BBA program." },
        { who: "agent", text: "Sure, let me check eligibility. May I have your 12th board percentage?" },
        { who: "caller", text: "I scored 78% in CBSE." },
        { who: "agent", text: "You qualify. Fees are ₹35,000/year. Shall I send the brochure on WhatsApp?" },
      ],
      notifs: [
        { type: "whatsapp", text: "Brochure + Application Link sent" },
        { type: "crm", text: "Lead created in admission CRM · Score 92" },
        { type: "schedule", text: "Counsellor callback queued · 4 PM tomorrow" },
      ],
    },
    faqs: [
      {
        q: "How quickly can a university go live with Fawks AI for admission season?",
        a: "Most universities go live in 14–21 days. Vidyasetu deployed in 18 days end-to-end — that includes assistant build, knowledge-base ingestion of brochure / fee / eligibility data, CRM API integration, and dry-runs with the admission team.",
      },
      {
        q: "Can the AI handle multilingual student calls?",
        a: "Yes — Aria handles Hindi, English and Marathi natively for Vidyasetu, with seamless code-switching. We support 70+ languages including Tamil, Telugu, Bengali, Kannada and Gujarati for other universities.",
      },
      {
        q: "Does Fawks AI integrate with our student information system (SIS)?",
        a: "We integrate with all major SIS / ERPs including Linways, AcadStaff, Camu, MasterSoft, and any system with a REST API. Live application status lookups happen mid-call without holds.",
      },
      {
        q: "How is student data handled — is it UGC / DPDP compliant?",
        a: "All conversations are encrypted at rest (AES-256) and in transit (TLS 1.3). Voice recordings can be auto-deleted or anonymised post-resolution. DPDP-aligned consent capture is built into the call flow.",
      },
      {
        q: "Can complex queries be transferred to a human counsellor?",
        a: "Yes — when the AI detects intent it can't resolve (fee waivers, scholarship eligibility, transcript verification), it warm-transfers to a counsellor with a 3-second context summary whispered before connect.",
      },
    ],
    seoTitle: "EdTech Case Study · Vidyasetu University 85% AI Automation | Fawks AI",
    seoDesc:
      "How Vidyasetu Open University automated 85% of student inquiries and 4× enrollment conversion with a bilingual Fawks AI voice agent in 18 days.",
  },
  "real-estate": {
    tagline: "AI voice qualification + WhatsApp site-visit automation",
    productViz: {
      title: "Aria · Sales Qualifier",
      meta: "Hindi-EN · Lumeon Properties",
      transcript: [
        { who: "agent", text: "Hi, this is Aria from Lumeon. You enquired about Bandra West, right?" },
        { who: "caller", text: "Yes, I want a 3BHK." },
        { who: "agent", text: "Great. What's your budget bracket — under ₹3.5 Cr okay?" },
        { who: "caller", text: "Yes, around that." },
        { who: "agent", text: "Shall I block a site visit this Saturday at 11 AM?" },
      ],
      notifs: [
        { type: "whatsapp", text: "Floor plan + brochure sent" },
        { type: "crm", text: "Hot Lead · ₹3.2 Cr · synced to Salesforce" },
        { type: "schedule", text: "Site visit booked · Saturday 11 AM" },
      ],
    },
    faqs: [
      {
        q: "How fast does the AI call leads after they submit a form?",
        a: "Average response time is under 60 seconds — significantly faster than the 6–8 hour industry baseline. Webhook from FB / MagicBricks / 99acres triggers the AI dial instantly.",
      },
      {
        q: "Can the AI qualify leads in regional languages?",
        a: "Yes. For Lumeon we deployed Hindi-English-Marathi code-switching. The AI can switch mid-call based on the caller's preferred language.",
      },
      {
        q: "Will the AI book site visits directly on agent calendars?",
        a: "Yes — Google Calendar, Outlook and HubSpot calendar are natively integrated. The AI checks live availability of the nearest closer and books the slot during the call.",
      },
      {
        q: "What happens to cold leads that don't qualify?",
        a: "Cold leads go into a 7-day drip nurture — automated outbound calls + WhatsApp follow-ups. Roughly 14% reactivate within the nurture window.",
      },
      {
        q: "Does Fawks AI integrate with our CRM?",
        a: "Native integrations include Salesforce, HubSpot, Zoho, LeadSquared. Custom CRMs connect via REST API or webhooks.",
      },
    ],
    seoTitle: "Real Estate Case Study · 72% AI Lead Qualification | Fawks AI",
    seoDesc:
      "How Lumeon Properties qualified 12,000+ property leads with Fawks AI voice agents — 72% qualification rate, 3.8× site-visit conversion, live in 12 days.",
  },
  healthcare: {
    tagline: "Appointment confirmations · no-show recovery",
    productViz: {
      title: "Maya · Patient Coordinator",
      meta: "Hindi-EN-Tamil · MediCircle",
      transcript: [
        { who: "agent", text: "Hello, this is Maya from MediCircle. Confirming Dr. Sharma at 4 PM tomorrow?" },
        { who: "caller", text: "Actually I can't make 4. Can it be later?" },
        { who: "agent", text: "Next slots are 5:30 PM tomorrow or 11 AM Thursday." },
        { who: "caller", text: "5:30 tomorrow works." },
        { who: "agent", text: "Done — rebooked. WhatsApp confirmation coming through." },
      ],
      notifs: [
        { type: "whatsapp", text: "Appointment confirmation + clinic map" },
        { type: "crm", text: "Slot rebooked · 5:30 PM tomorrow" },
        { type: "schedule", text: "Day-of reminder queued · 10 AM" },
      ],
    },
    faqs: [
      {
        q: "Is Fawks AI HIPAA / Indian health-data compliant?",
        a: "Yes. We support HIPAA-aligned workflows, encrypted PHI handling, BAA-style agreements, and DPDP-aligned consent capture for Indian clinics. On-prem / VPC deployment is available for hospitals.",
      },
      {
        q: "Can the AI reschedule appointments on the call?",
        a: "Yes. The AI checks live doctor availability via calendar integration and books the next slot during the call — no callbacks, no IVR loops.",
      },
      {
        q: "What languages are supported for patient calls?",
        a: "For MediCircle we deployed Hindi, English, Tamil and Telugu. We support 70+ languages with multiple voice options per language.",
      },
      {
        q: "Does the AI handle prescription refills and lab follow-ups?",
        a: "Yes — post-visit follow-ups can include prescription refill checks, lab-report ready notifications, and post-procedure recovery checks. Complex cases warm-transfer to a clinical coordinator.",
      },
      {
        q: "What's the typical deployment timeline for a clinic network?",
        a: "MediCircle deployed in 10 days. Smaller clinics go live in 7 days; multi-specialty hospitals with EHR integrations typically take 14–21 days.",
      },
    ],
    seoTitle: "Healthcare Case Study · 41% No-Show Reduction | Fawks AI",
    seoDesc:
      "How MediCircle Clinics automated 68% of appointment calls and cut no-shows by 41% across 14 cities with Fawks AI — live in 10 days.",
  },
  ecommerce: {
    tagline: "Cart recovery · COD verify · post-purchase",
    productViz: {
      title: "Riya · Conversion Specialist",
      meta: "English-Hindi · Velora Beauty",
      transcript: [
        { who: "agent", text: "Hi Priya, this is Riya from Velora. Saw you left the Vitamin C serum in cart." },
        { who: "caller", text: "Yeah, the shipping cost felt high." },
        { who: "agent", text: "I can add free shipping with code RIYA15 — 15% off too." },
        { who: "caller", text: "Done, that works for me." },
        { who: "agent", text: "Sending the checkout link on WhatsApp now." },
      ],
      notifs: [
        { type: "whatsapp", text: "Checkout link + coupon applied" },
        { type: "crm", text: "Cart recovered · ₹1,240 order" },
        { type: "schedule", text: "Delivery tracking SMS queued" },
      ],
    },
    faqs: [
      {
        q: "How does Fawks AI integrate with Shopify?",
        a: "Native Shopify integration captures abandoned-cart events via webhooks. Within 15 minutes the AI calls the customer with a personalised pitch referencing the specific items in their cart.",
      },
      {
        q: "Can the AI apply coupons during the call?",
        a: "Yes — the AI can dynamically apply discount codes, free-shipping rules or buy-one-get-one offers based on cart value and customer history.",
      },
      {
        q: "Will the AI confirm COD orders to reduce RTO?",
        a: "Yes. Voice + WhatsApp dual-verification for COD orders dropped Velora's RTO rate from 18% to 4%. The AI verifies address, delivery slot and intent before warehouse dispatch.",
      },
      {
        q: "How does this compare to email and WhatsApp template recovery?",
        a: "Email recovers 1–2% of carts. WhatsApp templates recover 3–4%. Fawks AI recovers 18–25% because it has a conversation, handles objections, and offers contextual incentives.",
      },
      {
        q: "Does it integrate with Klaviyo / Mailchimp / Yotpo?",
        a: "Yes — bidirectional sync with Klaviyo, Mailchimp, Yotpo, Judge.me and most major marketing-stack tools.",
      },
    ],
    seoTitle: "D2C Case Study · 23% Cart Recovery, 5.1× ROAS | Fawks AI",
    seoDesc:
      "How Velora Beauty recovered 23% of abandoned Shopify carts with Fawks AI voice agents — 5.1× ROAS, deployed in 9 days.",
  },
  bfsi: {
    tagline: "Collections · PTP capture · compliant calling",
    productViz: {
      title: "Vir · Recovery Specialist",
      meta: "Hindi-EN-Marathi · ProsperLend",
      transcript: [
        { who: "agent", text: "Namaste, this is Vir from ProsperLend. Your EMI of ₹8,240 is due since the 5th." },
        { who: "caller", text: "Sir, I'll pay next week." },
        { who: "agent", text: "Sure — can I log a Promise-to-Pay for 18th October?" },
        { who: "caller", text: "Yes, 18th confirmed." },
        { who: "agent", text: "Sending you a payment link on WhatsApp for early settlement." },
      ],
      notifs: [
        { type: "whatsapp", text: "Payment link · UPI / card" },
        { type: "crm", text: "PTP logged · 18-Oct · DPD 12" },
        { type: "schedule", text: "Follow-up reminder · 17-Oct" },
      ],
    },
    faqs: [
      {
        q: "Is Fawks AI TRAI-compliant for collection calls?",
        a: "Yes — calling windows, DND-scrub, and consent capture are auto-enforced. Audit trails are exportable for every call for regulator audits.",
      },
      {
        q: "Can the AI capture Promise-to-Pay (PTP) commitments?",
        a: "Yes. PTP date and amount are captured mid-call, validated against the borrower's history, and synced to the LMS / CRM. WhatsApp confirmation goes out within 30 seconds.",
      },
      {
        q: "How does the AI handle empathy and de-escalation?",
        a: "Scripts are tuned for empathetic tone in regional languages. The AI detects sentiment shifts and adapts pace, tone and offer accordingly. Hostile or vulnerable cases warm-transfer to a senior agent.",
      },
      {
        q: "Does it work for early-bucket (DPD 5–30) collections?",
        a: "Yes — early-bucket is where AI delivers the highest ROI. ProsperLend resolves 61% of DPD 5–30 cases entirely via AI, freeing agents for DPD 60+ negotiation.",
      },
      {
        q: "Can it be deployed on-prem or in a private VPC?",
        a: "Yes — for regulated lenders we offer on-prem or AWS / Azure VPC deployment with customer-managed encryption keys.",
      },
    ],
    seoTitle: "BFSI Case Study · 61% AI Collection Resolution | Fawks AI",
    seoDesc:
      "How ProsperLend NBFC automated 61% of collection calls and saved ₹4.1 Cr in roll-forward losses with Fawks AI — TRAI-compliant, live in 15 days.",
  },
  "hr-recruitment": {
    tagline: "Screen candidates 6× faster · interview booking",
    productViz: {
      title: "Nia · Talent Specialist",
      meta: "Hindi-EN · CodeWeave Staffing",
      transcript: [
        { who: "agent", text: "Hi Anand, this is Nia from CodeWeave. Calling about the React role at FinTech Co." },
        { who: "caller", text: "Yes, I applied yesterday." },
        { who: "agent", text: "Quick screen — what's your notice period and expected CTC?" },
        { who: "caller", text: "30 days notice, 22 LPA expected." },
        { who: "agent", text: "Perfect fit. Booking your interview on Thursday at 4 PM." },
      ],
      notifs: [
        { type: "whatsapp", text: "JD PDF + interview details sent" },
        { type: "crm", text: "Candidate scored · 87/100 · shortlist" },
        { type: "schedule", text: "Interview booked · Thursday 4 PM" },
      ],
    },
    faqs: [
      {
        q: "Can the AI screen for technical roles?",
        a: "Yes — JD-aware screening covers tech stack, years of experience, project depth, notice period, CTC, location and remote-readiness. Detailed scoring rubric is fully configurable per role.",
      },
      {
        q: "Does it book interviews on hiring-manager calendars?",
        a: "Yes — Google Calendar / Outlook / Calendly integrations let the AI book interview slots live, with reminder workflows for both candidate and panel.",
      },
      {
        q: "Can the AI call candidates after working hours?",
        a: "Yes. The AI works 24×7 — 38% of CodeWeave's applicants apply between 7 PM and midnight, and the AI screens them within 5 minutes of application.",
      },
      {
        q: "Will the AI replace recruiters?",
        a: "No — it removes the repetitive first-call screening so recruiters focus on closing, negotiation, and senior roles. CodeWeave freed 38% of recruiter capacity to do higher-value work.",
      },
      {
        q: "How is candidate data handled?",
        a: "Encrypted at rest and in transit. Candidate consent is captured at the start of every call. Recordings can be auto-deleted post-decision or stored per your retention policy.",
      },
    ],
    seoTitle: "HR Case Study · 6× Faster Candidate Screening | Fawks AI",
    seoDesc:
      "How CodeWeave Staffing screened 74% of candidates with Fawks AI voice — 6× faster shortlist turnaround, deployed in 8 days.",
  },
  logistics: {
    tagline: "Delivery slot calls · address verify · delay updates",
    productViz: {
      title: "Kabir · Logistics Coordinator",
      meta: "Hindi-EN-Bengali · TrackEdge",
      transcript: [
        { who: "agent", text: "Hi, this is Kabir from TrackEdge. Confirming your delivery for today between 4–7 PM?" },
        { who: "caller", text: "Yes, but is the address correct? Flat 504-A or 504-B?" },
        { who: "agent", text: "Updated to 504-B. Anything else for the driver?" },
        { who: "caller", text: "Yes, call before coming. The gate guard might be tricky." },
        { who: "agent", text: "Noted. Driver will call 10 minutes before arrival." },
      ],
      notifs: [
        { type: "whatsapp", text: "Tracking link + driver number" },
        { type: "crm", text: "Address updated · 504-B · note logged" },
        { type: "schedule", text: "Driver call-ahead reminder set" },
      ],
    },
    faqs: [
      {
        q: "Does Fawks AI integrate with our WMS / TMS?",
        a: "Yes — native integrations with Unicommerce, Shiprocket, Vinculum, ClickPost and most major Indian logistics stacks. Custom WMS / TMS integrate via REST API or webhooks.",
      },
      {
        q: "Can the AI handle delivery delay communications proactively?",
        a: "Yes. Weather, route or vehicle events trigger proactive outbound calls to affected customers with revised ETAs and reschedule options.",
      },
      {
        q: "Will it work in tier 2 and tier 3 cities?",
        a: "Yes — TrackEdge runs across 65 cities including small-town deployments. Multilingual support (6 Indian languages for TrackEdge) makes regional coverage seamless.",
      },
      {
        q: "Can it reduce return-to-origin (RTO) rates?",
        a: "Yes. Address verification + slot confirmation calls reduced TrackEdge's RTO from 9.3% to 7.3% (21% reduction) within 60 days.",
      },
      {
        q: "Does the AI work for B2B logistics too?",
        a: "Yes — we support B2B freight notifications, dock-slot booking, lorry-receipt updates, and POD-collection workflows.",
      },
    ],
    seoTitle: "Logistics Case Study · 82% Delivery Call Automation | Fawks AI",
    seoDesc:
      "How TrackEdge Logistics automated 82% of delivery-related calls and cut support tickets by 38% with Fawks AI — live in 11 days.",
  },
};

export const getExtras = (slug) => CASE_EXTRAS[slug] || null;
